import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import CodeMirror from './common/CodeMirrorWithWrap'
import { sql } from '@codemirror/lang-sql'
import { json } from '@codemirror/lang-json'
import { yaml } from '@codemirror/lang-yaml'
import { oneDark } from '@codemirror/theme-one-dark'
import { search, searchKeymap, selectSelectionMatches } from '@codemirror/search'
import { keymap } from '@codemirror/view'
import { Prec } from '@codemirror/state'
import { lookml } from '../lang-lookml'
import { copyToClipboard } from '../utils/clipboard'
import {
  setChangeMarkers,
  setDeleteDecos,
  changeMarkerField,
  deleteDecoField,
  changeGutter,
  computeChangeMarkers,
} from '../diff-gutter'

const LANG_MAP = {
  sql:    () => [sql()],
  json:   () => [json()],
  yaml:   () => [yaml()],
  lookml: () => [lookml()],
}

export default function OutputTabs({ lang, value, label, onChange, dirty, originalValue, yamlDirty, onConvert, isConverting, editorTheme }) {
  const [copied, setCopied] = useState(false)
  const editable = typeof onChange === 'function'
  const viewRef = useRef(null)
  const originalLines = useRef([])

  // originalValue 갱신 (생성/로드/저장/재변환 시) + 마커 재계산
  useEffect(() => {
    if (originalValue != null) {
      originalLines.current = originalValue.split('\n')
    }
    const view = viewRef.current
    if (view && originalLines.current.length > 0) {
      const result = computeChangeMarkers(view.state.doc, originalLines.current)
      view.dispatch({ effects: [
        setChangeMarkers.of(result.gutterMarkers),
        setDeleteDecos.of(result.deleteDecos),
      ] })
    }
  }, [originalValue])

  const extensions = useMemo(() => [
    ...(LANG_MAP[lang]?.() ?? []),
    search({ top: true }),
    Prec.highest(keymap.of([
      ...searchKeymap,
      { key: 'Mod-Shift-l', run: selectSelectionMatches, preventDefault: true },
    ])),
    changeMarkerField,
    deleteDecoField,
    changeGutter,
  ], [lang])

  const handleCopy = () => {
    if (!value) return
    copyToClipboard(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  const handleChange = useCallback((val, viewUpdate) => {
    onChange?.(val)
    const view = viewUpdate.view
    if (originalLines.current.length > 0) {
      const result = computeChangeMarkers(view.state.doc, originalLines.current)
      view.dispatch({ effects: [
        setChangeMarkers.of(result.gutterMarkers),
        setDeleteDecos.of(result.deleteDecos),
      ] })
    }
  }, [onChange])

  const handleCreateEditor = useCallback((view) => {
    viewRef.current = view
    if (originalLines.current.length > 0) {
      const result = computeChangeMarkers(view.state.doc, originalLines.current)
      view.dispatch({ effects: [
        setChangeMarkers.of(result.gutterMarkers),
        setDeleteDecos.of(result.deleteDecos),
      ] })
    }
  }, [])

  if (!value) {
    return (
      <div className="output-panel">
        <div className="placeholder">
          뷰를 생성하면 여기에 {label}이 표시됩니다.
        </div>
      </div>
    )
  }

  return (
    <div className="output-panel">
      {yamlDirty && onConvert && (
        <div className="reconvert-banner">
          <span>Semantic View YAML이 수정되었습니다. 변경사항을 반영하려면 재변환하세요.</span>
          <button className="btn-reconvert" onClick={onConvert} disabled={isConverting}>
            {isConverting ? '변환 중...' : '⟳ 전체 재변환'}
          </button>
        </div>
      )}
      <div className="output-label">
        <span>
          {label}
          {dirty && <span className="modified-dot-sm" title="수정됨" />}
        </span>
        <button className="btn-copy" onClick={handleCopy}>
          {copied ? '✓ 복사됨' : '복사'}
        </button>
      </div>
      <div className="output-editor">
        <CodeMirror
          value={value}
          height="100%"
          theme={editorTheme || oneDark}
          extensions={extensions}
          readOnly={!editable}
          onChange={editable ? handleChange : undefined}
          onCreateEditor={handleCreateEditor}
          basicSetup={{
            lineNumbers: true,
            foldGutter: editable,
            highlightActiveLine: editable,
            autocompletion: false,
          }}
        />
      </div>
    </div>
  )
}
